/**
 * dishResource — the dish catalog, authored against the resource-kit v2
 * model/view contracts and consumed directly as a `ResourceBundle` by
 * `ResourcePage` (#1805 slice A2).
 *
 * Unlike the slice A1 resources this one carries no `fields[].render`, no
 * `transform` and no `copyable`, so no `widgets.tsx` appears alongside it and
 * every cell keeps resolving through its field type. What the migration really
 * does is split the one flat object into the three v2 halves:
 *
 *   - `dishModel` owns the record — the field set, their types, the option
 *     tables and the API. These are facts about a dish, true on every surface.
 *   - `dishListView` owns the ONE ordered `items` sequence, which is also where
 *     the visibility modifiers live: `ResourcePage` derives both table and
 *     record columns from this sequence, so `columnInvisible` / `invisible`
 *     have exactly one home.
 *   - `dishFormView` owns the record view — the field groups plus the recipe
 *     tab. Field groups are a partition of field NAMES, which is why each
 *     group member here carries nothing but its name.
 *
 * The recipe editor is the dish's second half. It is the only tab, so the rail
 * opens on it: operators still see the dish identity plus the recipe editor on
 * one screen, with no extra navigation.
 *
 * `searchable` is deliberately absent, exactly as before — but NOT for the
 * reason this comment used to give. The route does declare a search column
 * (`catalog-dishes.ts` `searchable: ["name"]`, codegen'd as
 * `CATALOG_DISH_SEARCH`), so a search box would work; this list has simply
 * never offered one, and turning it on is a UI decision nobody has made.
 * Declaring `capabilities.search: false` instead would freeze today's
 * framework default as a declaration, which is why the key stays absent.
 * `__tests__/catalogResources.v2-parity.test.tsx` freezes that, the cell HTML,
 * the CSV text and the column order against a golden captured before the
 * migration. The filter schema is frozen there too, but is no longer that
 * capture: #2114 re-derived it from the whitelist below, so it now pins the
 * ONE column this list may offer rather than the eleven it used to hand over.
 */
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
  type EnumOption,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import type { Dish } from '@/services';
import { dishAPI, toListResult } from '@/services';
import { DISH_CATEGORIES } from '@/services/catalog-enums';
import { CATALOG_DISH_SORTS } from '@/services/filters.generated';
import { PERMISSIONS } from '@/permissions';
import { RecipeEditorCard } from './components/RecipeEditorCard';

const statusOptions: EnumOption[] = [
  { value: 'active', label: 'dish.enum.status.active', color: 'success' },
  { value: 'archived', label: 'dish.enum.status.archived', color: 'secondary' },
];

// `category` enum mirrors the backend `DISH_CATEGORIES` closed-set
// (#80) via the `catalog-enums.ts` bridge; previously hardcoded
// here in 5 lines, drift-prone (#83 method B).
const categoryOptions: EnumOption[] = DISH_CATEGORIES.map((c) => ({
  value: c,
  label: `dish.enum.category.${c}`,
}));

const f = createFieldConstructors<Dish>();

export const dishModel = defineModel<Dish>()({
  name: 'dish',
  idField: 'id',
  recName: 'name',
  fields: {
    id: f.id({ label: 'dish.field.id' }),
    name: f.text({ label: 'dish.field.name' }),
    name_zh: f.text({ label: 'dish.field.nameZh' }),
    name_en: f.text({ label: 'dish.field.nameEn' }),
    category: f.enum({ label: 'dish.field.category', options: categoryOptions }),
    portion_grams: f.number({ label: 'dish.field.portionGrams' }),
    cook_time_min: f.number({ label: 'dish.field.cookTimeMin' }),
    description: f.text({ label: 'dish.field.description' }),
    status: f.enum({ label: 'dish.field.status', options: statusOptions }),
    created_at: f.datetime({ label: 'dish.field.createdAt' }),
    updated_at: f.datetime({ label: 'dish.field.updatedAt' }),
  },
});

export const dishListView = defineListView(dishModel, {
  items: [
    { field: 'id', columnInvisible: true, invisible: true },
    { field: 'name' },
    { field: 'name_zh', columnInvisible: true },
    { field: 'name_en', columnInvisible: true },
    { field: 'category' },
    { field: 'portion_grams' },
    { field: 'cook_time_min', columnInvisible: true },
    { field: 'description', columnInvisible: true },
    { field: 'status' },
    { field: 'created_at', columnInvisible: true, invisible: true },
    { field: 'updated_at', columnInvisible: true, invisible: true },
  ],
  defaultSort: { field: 'created_at', dir: 'desc' },
});

// Every EDITABLE field lives in the first tab (#1922). The former
// `preparation` group was a second rail trigger holding three more inputs of
// the same edit, so an operator could fill in the identity half, save, and
// never see the rest. `meta` stays behind: both of its fields are
// `serverAssigned` and therefore read-only on every surface.
export const dishFormView = defineFormView(dishModel, {
  groupDisplay: 'tab',
  children: [
    {
      kind: 'fields',
      id: 'identity',
      label: 'dish.group.identity',
      items: [
        { field: 'name' },
        { field: 'name_zh' },
        { field: 'name_en' },
        { field: 'category' },
        { field: 'status' },
        { field: 'portion_grams' },
        { field: 'cook_time_min' },
        { field: 'description' },
      ],
    },
    {
      kind: 'fields',
      id: 'meta',
      label: 'dish.group.meta',
      // `id` sits with the other server-assigned stamps rather than in
      // `identity`: it is a read-only UUID, and `identity` is the edit form.
      items: [{ field: 'id' }, { field: 'created_at' }, { field: 'updated_at' }],
    },
    {
      kind: 'custom',
      id: 'recipe',
      label: 'dish.recipe.title',
      render: function DishRecipeTab({ record }) {
        return <RecipeEditorCard dish={record} />;
      },
    },
  ],
});

export const dishResource: ResourceBundle<Dish, typeof dishModel.fields> = {
  model: dishModel,
  list: dishListView,
  form: dishFormView,
  host: {
    api: {
      // The list page passes straight through (#1909); only the unwrapped
      // detail record still needs the legacy `ApiResponse` bridge (#1151).
      list: async (params) => toListResult(await dishAPI.list(params)),
      get: (id) => dishAPI.detail(id),
      create: (body) => dishAPI.create(body),
      update: (id, body) => dishAPI.update(id, body),
      delete: (id) => dishAPI.delete(id),
    },
    label: 'dish.label',
    slug: 'catalog-dishes',
    listTitle: 'dish.listTitle',
    listDescription: 'dish.listDescription',
    // dishes re-gated onto `catalog:read` — the backend folded the legacy
    // `dishes:*` keys into the unified `catalog:*` module (#943).
    permission: {
      list: PERMISSIONS.CATALOG_READ,
      read: PERMISSIONS.CATALOG_READ,
      // Mutations require `catalog:write` (backend write routes enforce it);
      // gating the buttons here stops a read-only viewer seeing a control
      // the server would only 403 — the repo's forbidden deceptive-affordance.
      create: PERMISSIONS.CATALOG_WRITE,
      update: PERMISSIONS.CATALOG_WRITE,
      delete: PERMISSIONS.CATALOG_WRITE,
    },
    // `status` and NOTHING else (#2114). The reason is not `x-filterable` —
    // that seam advertises the kernel's `filter[field][op]=` DSL, which this
    // route does not parse, which is why the backend leaves it empty on
    // purpose. The reason is that `/api/catalog/dishes` declares a bare
    // `status=` query parameter and `listDishes` applies it
    // (`contexts/kitchen/http/catalog-dishes.ts`), and `dishAPI.list` already
    // whitelists exactly that. Omitting this key reads as "unrestricted":
    // measured through `ResourcePage` before this line existed, the field menu
    // offered all ELEVEN columns, ten of which `toApiListParams` dropped
    // before the request, unfiltered and unwarned. This narrows the menu to
    // the one that works. Pinned to `eq` BY CONSTRUCTION, not by luck:
    // `serializeConditionsToParams` emits `eq` as a bare value, which is what
    // this parameter is; every other operator would arrive as `ne:archived` /
    // `in:a,b` and the route's zod enum would answer 400.
    filterable: { status: ['eq'] },
    // The same seam, one face over (#2114). `/api/catalog/dishes` declares
    // `x-sortable: []` and its handler never reads a sort param, but an
    // ABSENT declaration made `createColumns` mark every header sortable.
    // Losing those headers is the point: each one issued a sort the route
    // ignores, which on screen is indistinguishable from a sort that ran.
    // Bound by reference so regeneration flows through (#1837 guardrail A).
    sortable: CATALOG_DISH_SORTS,
    searchPlaceholder: 'dish.searchPlaceholder',
    formConfig: {
      createTitle: 'dish.form.createTitle',
      editTitle: 'dish.form.editTitle',
      columns: 2,
      maxWidth: 'md',
    },
  },
};
